import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DashboardDto,
  NavigationDashboardTabItemDto,
} from '@asset-metrix/portal-gateway-client-js';
import { AsyncNotification } from '@asset-metrix/portal-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  invalidateDashboardListQueries,
  persistAllDashboardsModalChanges,
} from '../../api/portalGateway/dashboard';
import {
  applyDashboardNavCacheChange,
  buildDashboardNavItemsAfterModalSave,
  getCachedDashboardNavItems,
} from '../../api/portalGateway/navigation';
import { useStore } from '../../store';
import { isNotNull, isNull } from '../../utils/nullish';

import { DashboardModalRow } from './utils/dashboard.types';
import { AllDashboardsModalUI } from './AllDashboardsModalUI';
import { usePageDashboardsQuery } from './usePageDashboardsQuery';

type AllDashboardsModalProps = {
  opened: boolean;
  sourcePageId: string;
  dashboardNavItems: NavigationDashboardTabItemDto[] | undefined;
  activeDashboardId: string | null | undefined;
  onNavigateToDashboard: (dashboardId: string | null) => void;
  onClose: () => void;
};

type DashboardDeleteTarget = {
  id: string;
  name: string;
};

export function getInitialInstancedDashboardIds(
  dashboardNavItems: readonly NavigationDashboardTabItemDto[]
): string[] {
  return [...dashboardNavItems]
    .sort((a, b) => a.order - b.order)
    .map((item) => item.dashboardId);
}

function dashboardIdListsEqual(
  a: readonly string[],
  b: readonly string[]
): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

export function buildDashboardModalSectionsFromDraft(
  pageDashboards: readonly DashboardDto[],
  draftInstancedDashboardIds: readonly string[],
  pendingDashboardDeletes: ReadonlySet<string>
) {
  const dashboardById = new Map(pageDashboards.map((row) => [row.id, row]));
  const instancedSet = new Set(draftInstancedDashboardIds);

  const instancedRows: DashboardModalRow[] = draftInstancedDashboardIds.flatMap(
    (dashboardId, index) => {
      const dashboard = dashboardById.get(dashboardId);
      if (pendingDashboardDeletes.has(dashboardId) || isNull(dashboard)) {
        return [];
      }
      return [{ ...dashboard, instanceOrder: index }];
    }
  );

  const availableRows = [...pageDashboards]
    .filter((dashboard) => !instancedSet.has(dashboard.id))
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );

  return { instancedRows, availableRows };
}

export function AllDashboardsModal({
  opened,
  sourcePageId,
  dashboardNavItems,
  activeDashboardId,
  onNavigateToDashboard,
  onClose,
}: AllDashboardsModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentUserId = useStore(
    useShallow((state) => state.session?.getUserId())
  );

  const isDashboardOwner = (ownerUserId: number) =>
    isNotNull(currentUserId) && ownerUserId === currentUserId;

  const { data: pageDashboards, isPending: isDashboardsPending } =
    usePageDashboardsQuery(sourcePageId, { enabled: opened });

  const [deleteTarget, setDeleteTarget] =
    useState<DashboardDeleteTarget | null>(null);
  const [draftInstancedDashboardIds, setDraftInstancedDashboardIds] = useState<
    string[]
  >([]);
  const [initialInstancedDashboardIds, setInitialInstancedDashboardIds] =
    useState<string[]>([]);
  const [pendingDashboardDeletes, setPendingDashboardDeletes] = useState<
    Set<string>
  >(() => new Set());

  const wasOpenedRef = useRef(false);

  useEffect(() => {
    if (opened && !wasOpenedRef.current) {
      const initial = getInitialInstancedDashboardIds(dashboardNavItems ?? []);
      setDraftInstancedDashboardIds(initial);
      setInitialInstancedDashboardIds(initial);
      setPendingDashboardDeletes(new Set());
      setDeleteTarget(null);
    }
    wasOpenedRef.current = opened;
  }, [opened, dashboardNavItems]);

  const isDirty =
    pendingDashboardDeletes.size > 0 ||
    !dashboardIdListsEqual(
      draftInstancedDashboardIds,
      initialInstancedDashboardIds
    );

  const saveMutation = useMutation({
    mutationFn: () =>
      persistAllDashboardsModalChanges({
        sourcePageId,
        draftInstancedDashboardIds,
        pendingDashboardDeletes,
      }),
    onSuccess: async (result) => {
      if (result.succeededDeleteIds.length > 0) {
        setPendingDashboardDeletes((current) => {
          const next = new Set(current);
          for (const id of result.succeededDeleteIds) {
            next.delete(id);
          }
          return next;
        });
      }

      const orderedDashboardIds = result.finalInstancedDashboardIds.filter(
        (dashboardId) => !result.succeededDeleteIds.includes(dashboardId)
      );
      const labelByDashboardId = new Map(
        (pageDashboards ?? []).map((row) => [row.id, row.name])
      );
      applyDashboardNavCacheChange(queryClient, sourcePageId, {
        op: 'replace',
        items: buildDashboardNavItemsAfterModalSave({
          orderedDashboardIds,
          instanceByDashboardId: result.instanceByDashboardId,
          existingNavItems: getCachedDashboardNavItems(
            queryClient,
            sourcePageId
          ),
          labelByDashboardId,
        }),
      });
      await invalidateDashboardListQueries(queryClient, sourcePageId);

      if (result.hasFailures) {
        AsyncNotification.show(
          { message: t('app.dashboard.allDashboardsModal.actionFailed') },
          'error'
        );
        return;
      }

      AsyncNotification.show(
        { message: t('app.dashboard.allDashboardsModal.saveSuccess') },
        'success'
      );
      if (
        isNotNull(activeDashboardId) &&
        !result.finalInstancedDashboardIds.includes(activeDashboardId)
      ) {
        onNavigateToDashboard(result.finalInstancedDashboardIds[0] ?? null);
      }
      onClose();
    },
    onError: () => {
      AsyncNotification.show(
        { message: t('app.dashboard.allDashboardsModal.actionFailed') },
        'error'
      );
    },
  });

  const handleHide = (dashboardId: string) => {
    setDraftInstancedDashboardIds((current) =>
      current.filter((id) => id !== dashboardId)
    );
  };

  const handleShow = (dashboardId: string) => {
    if (pendingDashboardDeletes.has(dashboardId)) {
      return;
    }
    setDraftInstancedDashboardIds((current) =>
      current.includes(dashboardId) ? current : [...current, dashboardId]
    );
  };

  const handleMove = (dashboardId: string, direction: -1 | 1) => {
    setDraftInstancedDashboardIds((current) => {
      const index = current.indexOf(dashboardId);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= current.length) {
        return current;
      }
      const next = [...current];
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const handleDeleteConfirm = () => {
    if (isNull(deleteTarget)) {
      return;
    }

    const { id } = deleteTarget;
    setPendingDashboardDeletes((current) => new Set(current).add(id));
    setDraftInstancedDashboardIds((current) =>
      current.filter((dashboardId) => dashboardId !== id)
    );
    setDeleteTarget(null);
  };

  const handleRestore = (dashboardId: string) => {
    setPendingDashboardDeletes((current) => {
      if (!current.has(dashboardId)) {
        return current;
      }
      const next = new Set(current);
      next.delete(dashboardId);
      return next;
    });
  };

  const { instancedRows, availableRows } = isNotNull(pageDashboards)
    ? buildDashboardModalSectionsFromDraft(
        pageDashboards,
        draftInstancedDashboardIds,
        pendingDashboardDeletes
      )
    : { instancedRows: [], availableRows: [] };

  return (
    <AllDashboardsModalUI
      actionsDisabled={saveMutation.isPending}
      availableRows={availableRows}
      deleteConfirmation={{
        opened: isNotNull(deleteTarget),
        dashboardName: deleteTarget?.name ?? '',
        loading: saveMutation.isPending,
        onCancel: () => setDeleteTarget(null),
        onConfirm: () => handleDeleteConfirm(),
      }}
      instancedRows={instancedRows}
      isDashboardOwner={isDashboardOwner}
      isMarkedForDeletion={(dashboardId: string) =>
        pendingDashboardDeletes.has(dashboardId)
      }
      loading={isDashboardsPending && isNull(pageDashboards)}
      opened={opened}
      pendingActionForRow={() => null}
      saveDisabled={!isDirty || saveMutation.isPending}
      saveLoading={saveMutation.isPending}
      onClose={onClose}
      onHide={handleHide}
      onMoveDown={(dashboardId: string) => handleMove(dashboardId, 1)}
      onMoveUp={(dashboardId: string) => handleMove(dashboardId, -1)}
      onRequestDelete={(id: string, name: string) =>
        setDeleteTarget({ id, name })
      }
      onRestore={handleRestore}
      onSave={() => saveMutation.mutate()}
      onShow={handleShow}
    />
  );
}
